import { CommonModule } from '@angular/common';
import { Component, inject, OnInit, signal } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { IstDatePipe } from '../../../pipes/ist-date.pipe';
import { SnackbarService } from '../../../services/design/snackbar.service';
import {
  AdminRefundRequest,
  CreateRefundRequestInput,
  RefundListResponse,
  RefundRequestedVia,
  RefundRequestStatus,
  RefundRequestsService,
} from '../../../services/orders/refund-requests.service';
import { StoreOption, StoreService } from '../../../services/stores/store.service';
import { IndianCompactPipe } from '../../../pipes/indian-compact.pipe';
import { AuthService } from '../../../services/auth/auth.service';

@Component({
  selector: 'app-refund-requests',
  standalone: true,
  imports: [CommonModule, FormsModule, IstDatePipe, IndianCompactPipe],
  templateUrl: './refund-requests.component.html',
})
export class RefundRequestsComponent implements OnInit {
  readonly auth=inject(AuthService);
  private refundService = inject(RefundRequestsService);
  private storeService = inject(StoreService);
  private snackbar = inject(SnackbarService);
  private route = inject(ActivatedRoute, { optional: true });

  requests = signal<AdminRefundRequest[]>([]);
  totals = signal<RefundListResponse['totals']>({
    requested: 0,
    pending: 0,
    approved: 0,
    refunded: 0,
    rejected: 0,
  });
  stores = signal<StoreOption[]>([]);

  activeFilter: RefundRequestStatus = 'ALL';
  selectedStore = 0;
  customerSearch = '';
  fromDate = '';
  toDate = '';
  page = 1;
  pageSize = 10;
  totalItems = 0;
  Math = Math;

  showCreateModal = signal(false);
  createForm = {
    orderRef: '',
    amount: '',
    reason: '',
    requestedVia: 'phone' as RefundRequestedVia,
    remarks: '',
  };

  actionRequest = signal<AdminRefundRequest | null>(null);
  actionType = signal<'reject' | 'refunded' | null>(null);
  actionRemarks = '';
  actionTransactionRef = '';
  isSubmitting = signal(false);
  historyData = signal<unknown>(null);

  readonly statusTabs: RefundRequestStatus[] = [
    'ALL',
    'pending',
    'approved',
    'processing',
    'failed',
    'refunded',
    'rejected',
  ];

  readonly requestedViaOptions: RefundRequestedVia[] = [
    'phone',
    'email',
    'whatsapp',
    'admin',
  ];

  ngOnInit(): void {
    const params = this.route?.snapshot.queryParamMap;
    this.customerSearch = params?.get('search')?.trim() || '';
    const store = Number(params?.get('store'));
    if (Number.isSafeInteger(store) && store > 0) this.selectedStore = store;
    if (!this.auth.isSuperAdmin()) this.selectedStore=Number(this.auth.storeId());
    this.storeService.allStores().subscribe({
      next: (data) => this.stores.set(data.filter(s=>this.auth.isSuperAdmin()||Number(s.id)===this.selectedStore)),
    });
    this.loadRequests();
  }

  loadRequests() {
    this.refundService
      .list({
        status: this.activeFilter,
        storeId: this.selectedStore > 0 ? this.selectedStore : undefined,
        fromDate: this.fromDate ? new Date(this.fromDate).toISOString() : undefined,
        toDate: this.toDate
          ? new Date(`${this.toDate}T23:59:59`).toISOString()
          : undefined,
        page: this.page,
        pageSize: this.pageSize,
        search: this.customerSearch,
      })
      .subscribe({
        next: (res) => {
          this.requests.set(res.data);
          this.totalItems = res.count;
          this.totals.set(res.totals);
        },
        error: (err) => {
          this.snackbar.error(err?.message ?? 'Failed to load refund requests');
        },
      });
  }

  setFilter(filter: RefundRequestStatus) {
    this.activeFilter = filter;
    this.page = 1;
    this.loadRequests();
  }

  onPageChange(newPage: number) {
    this.page = newPage;
    this.loadRequests();
  }

  get totalPages() {
    return Math.ceil(this.totalItems / this.pageSize);
  }

  statusClass(status: string): string {
    switch (status?.toLowerCase()) {
      case 'pending':
        return 'badge-warning';
      case 'approved':
        return 'badge-info';
      case 'refunded':
        return 'badge-success';
      case 'rejected':
        return 'badge-error';
      default:
        return 'badge-ghost';
    }
  }

  openCreateModal() {
    this.createForm = {
      orderRef: '',
      amount: '',
      reason: '',
      requestedVia: 'phone',
      remarks: '',
    };
    this.showCreateModal.set(true);
  }

  closeCreateModal() {
    this.showCreateModal.set(false);
  }

  submitCreate() {
    const orderRef = this.createForm.orderRef.trim();
    if (!orderRef || !this.createForm.reason.trim()) {
      this.snackbar.warning('Order number and reason are required');
      return;
    }

    const input: CreateRefundRequestInput = {
      orderRef,
      reason: this.createForm.reason.trim(),
      requestedVia: this.createForm.requestedVia,
      remarks: this.createForm.remarks.trim() || undefined,
    };

    if (this.createForm.amount.trim()) {
      input.amount = Number(this.createForm.amount);
    }

    this.isSubmitting.set(true);
    this.refundService.create(input).subscribe({
      next: () => {
        this.snackbar.success('Refund request created');
        this.closeCreateModal();
        this.loadRequests();
        this.isSubmitting.set(false);
      },
      error: (err) => {
        this.snackbar.error(err?.message ?? 'Failed to create refund request');
        this.isSubmitting.set(false);
      },
    });
  }

  approveRequest(request: AdminRefundRequest) {
    if (!confirm(`Approve refund request #${request.id}?`)) return;

    this.isSubmitting.set(true);
    this.refundService.approve(request.id).subscribe({
      next: () => {
        this.snackbar.success('Refund request approved');
        this.loadRequests();
        this.isSubmitting.set(false);
      },
      error: (err) => {
        this.snackbar.error(err?.message ?? 'Failed to approve refund');
        this.isSubmitting.set(false);
      },
    });
  }

  openReject(request: AdminRefundRequest) {
    this.actionRequest.set(request);
    this.actionType.set('reject');
    this.actionRemarks = '';
  }

  canMarkRefunded(request: AdminRefundRequest): boolean {
    return (
      (request.status === 'approved' || request.status === 'failed') &&
      String(request.order_payment_status ?? '').toLowerCase() === 'paid'
    );
  }

  openMarkRefunded(request: AdminRefundRequest) {
    if (!this.canMarkRefunded(request)) {
      this.snackbar.warning(
        'Mark refunded is only available for paid orders'
      );
      return;
    }

    this.actionRequest.set(request);
    this.actionType.set('refunded');
    this.actionRemarks = '';
    this.actionTransactionRef = '';
  }

  closeActionModal() {
    this.actionRequest.set(null);
    this.actionType.set(null);
    this.actionRemarks = '';
    this.actionTransactionRef = '';
  }

  submitAction() {
    const request = this.actionRequest();
    const type = this.actionType();
    if (!request || !type) return;

    this.isSubmitting.set(true);

    if (type === 'reject') {
      if (!this.actionRemarks.trim()) {
        this.snackbar.warning('Rejection reason is required');
        this.isSubmitting.set(false);
        return;
      }

      this.refundService.reject(request.id, this.actionRemarks.trim()).subscribe({
        next: () => {
          this.snackbar.success('Refund request rejected');
          this.closeActionModal();
          this.loadRequests();
          this.isSubmitting.set(false);
        },
        error: (err) => {
          this.snackbar.error(err?.message ?? 'Failed to reject refund');
          this.isSubmitting.set(false);
        },
      });
      return;
    }

    if (!this.actionTransactionRef.trim()) {
      this.snackbar.warning('Transaction reference is required');
      this.isSubmitting.set(false);
      return;
    }

    this.refundService
      .markRefunded(
        request.id,
        this.actionTransactionRef.trim(),
        this.actionRemarks.trim(),
        Number(request.approved_amount)
      )
      .subscribe({
        next: () => {
          this.snackbar.success('Manual external refund recorded');
          this.closeActionModal();
          this.loadRequests();
          this.isSubmitting.set(false);
        },
        error: (err) => {
          this.snackbar.error(err?.message ?? 'Failed to mark refund');
          this.isSubmitting.set(false);
        },
      });
  }

  processRefund(request: AdminRefundRequest): void {
    if (this.isSubmitting()) return;
    this.isSubmitting.set(true);
    this.refundService.process(request.id).subscribe({
      next: result => {
        this.isSubmitting.set(false);
        this.snackbar.success(`Refund: ${result.status}. ${result.message || ''}`);
        this.loadRequests();
      },
      error: error => {
        this.isSubmitting.set(false);
        this.snackbar.error(error.message || 'Could not check refund; refresh before retrying');
        this.loadRequests();
      },
    });
  }

  showHistory(request: AdminRefundRequest): void {
    this.refundService.history(request.id).subscribe({
      next: history => this.historyData.set(history),
      error: error => this.snackbar.error(error.message),
    });
  }
}
